import { AxiosInstance } from 'axios';
import { result, Result } from 'util/result';
import Terra from '../Terra';
import { ModulesResponse } from 'data/terra/api/terra/response/ModuleResponse';
import { ModuleResponse } from 'data/terra/api/terra/response/ModuleResponse';
import { CreateModuleRequest } from 'data/terra/Module';
import {
  ModuleVersionResponse,
  ModuleVersionsResponse,
} from 'data/terra/api/terra/response/ModuleVersionResponse';
import {
  ModuleVersionConfigResponse,
  ModuleVersionConfigsResponse,
} from 'data/terra/api/terra/response/ModuleVersionConfigResponse';
import { UploadAssetsRequest } from 'data/terra/api/terra/request/UploadAssetsRequest';
import { ModuleConfigDescription } from 'data/terra/ModuleConfig';
import * as ModuleUtil from '../util/ModuleUtil';

class ModuleEndpoint {
  getModulesUrl = () =>
    `${Terra.TERRA_HOST}/${Terra.TERRA_BASE_API_URL}/modules`;

  getModuleDetailUrl = (moduleId: string) =>
    `${Terra.TERRA_HOST}/${Terra.TERRA_BASE_API_URL}/modules/${moduleId}`;

  getModuleVersionsUrl = (moduleId: string) =>
    `${Terra.TERRA_HOST}/${Terra.TERRA_BASE_API_URL}/modules/${moduleId}/versions`;

  getModuleVersionConfigsUrl = (moduleId: string, versionId: string) =>
    `${Terra.TERRA_HOST}/${Terra.TERRA_BASE_API_URL}/modules/${moduleId}/versions/${versionId}/configs`;

  getModuleVersionConfigDetailUrl = (
    moduleId: string,
    versionId: string,
    configId: string
  ) =>
    `${Terra.TERRA_HOST}/${Terra.TERRA_BASE_API_URL}/modules/${moduleId}/versions/${versionId}/configs/${configId}`;

  getModuleVersionConfigAssetsUrl = (moduleId: string, versionId: string) =>
    `${Terra.TERRA_HOST}/${Terra.TERRA_BASE_API_URL}/modules/${moduleId}/versions/${versionId}/configs/upload`;
}

export class ModuleApi {
  terraClient: AxiosInstance;
  endpoint = new ModuleEndpoint();

  constructor(client: AxiosInstance) {
    this.terraClient = client;
  }

  fetchModules(productId?: string): Promise<Result<ModulesResponse>> {
    return result(
      async () =>
        (
          await this.terraClient.get<ModulesResponse>(
            this.endpoint.getModulesUrl(),
            {
              params: productId
                ? {
                    productId,
                    pageSize: 100,
                  }
                : {
                    pageSize: 100,
                  },
            }
          )
        ).data
    );
  }

  fetchModuleDetail(moduleId: string): Promise<Result<ModuleResponse>> {
    return result(
      async () =>
        (
          await this.terraClient.get<ModuleResponse>(
            this.endpoint.getModuleDetailUrl(moduleId)
          )
        ).data
    );
  }

  createModule(request: CreateModuleRequest): Promise<Result<ModuleResponse>> {
    return result(
      async () =>
        (
          await this.terraClient.post<ModuleResponse>(
            this.endpoint.getModulesUrl(),
            request
          )
        ).data
    );
  }

  fetchModuleVersions(
    moduleId: string
  ): Promise<Result<ModuleVersionsResponse>> {
    return result(
      async () =>
        (
          await this.terraClient.get<ModuleVersionsResponse>(
            this.endpoint.getModuleVersionsUrl(moduleId),
            {
              params: {
                pageSize: 50,
              },
            }
          )
        ).data
    );
  }

  createModuleVersion(
    moduleId: string,
    code: string
  ): Promise<Result<ModuleVersionResponse>> {
    return result(
      async () =>
        (
          await this.terraClient.post<ModuleVersionResponse>(
            this.endpoint.getModuleVersionsUrl(moduleId),
            { code }
          )
        ).data
    );
  }

  createModuleVersionConfig(
    moduleId: string,
    moduleVersionId: string,
    envId: string,
    config: ModuleConfigDescription<any, any>
  ): Promise<Result<ModuleVersionConfigResponse>> {
    return result(
      async () =>
        (
          await this.terraClient.post<ModuleVersionConfigResponse>(
            this.endpoint.getModuleVersionConfigsUrl(moduleId, moduleVersionId),
            {
              ...ModuleUtil.normalize(config),
              moduleVersion: {
                id: moduleVersionId,
              },
              env: {
                id: envId,
              },
            }
          )
        ).data
    );
  }

  updateModuleVersionConfig(
    moduleId: string,
    moduleVersionId: string,
    configId: string,
    config: ModuleConfigDescription<any, any>
  ): Promise<Result<ModuleVersionConfigResponse>> {
    return result(
      async () =>
        (
          await this.terraClient.put<ModuleVersionConfigResponse>(
            this.endpoint.getModuleVersionConfigDetailUrl(
              moduleId,
              moduleVersionId,
              configId
            ),
            ModuleUtil.normalize(config)
          )
        ).data
    );
  }

  uploadModuleVersionAssets(
    moduleId: string,
    moduleVersionId: string,
    request: UploadAssetsRequest
  ): Promise<Result<ModuleVersionConfigResponse>> {
    return result(
      async () =>
        (
          await this.terraClient.put<ModuleVersionConfigResponse>(
            this.endpoint.getModuleVersionConfigAssetsUrl(
              moduleId,
              moduleVersionId
            ),
            request
          )
        ).data
    );
  }

  fetchModuleVersionConfigs(
    moduleId: string,
    moduleVersionId: string
  ): Promise<Result<ModuleVersionConfigsResponse>> {
    return result(
      async () =>
        (
          await this.terraClient.get<ModuleVersionConfigsResponse>(
            this.endpoint.getModuleVersionConfigsUrl(moduleId, moduleVersionId),
            { params: { pageSize: 50 } }
          )
        ).data
    );
  }
}